import { getI18N, getLocale } from '@/ui/i18n/i18n';

export type KnownI18nDateTimeFormat = 'shortDate' | 'shortTime' | 'shortDateTime';

const sanitizeShortDate = (finalOptions: Intl.DateTimeFormatOptions): Intl.DateTimeFormatOptions => {
  const keysToKeep = new Set(['day', 'month', 'year']);

  return Object.fromEntries(
    Object.entries(finalOptions).filter(([key]) => keysToKeep.has(key)),
  );
};

function sanitizeShortTime(optionsWithoutTimeZone: Intl.DateTimeFormatOptions) {
  const { hour, minute, second, hour12, hourCycle } = optionsWithoutTimeZone;

  return {
    hour: hour ?? 'numeric',
    minute: minute ?? '2-digit',
    ...(second ? { second } : {}),
    ...(hour12 ? { hour12 } : {}),
    ...(hourCycle ? { hourCycle } : {}),
  };
}

export function formatSameAsI18nWithoutTimeZone(dateValue: Date, formatKey: KnownI18nDateTimeFormat): string {
  const i18nInstance = getI18N();
  const currentLocale = getLocale();

  const allFormats = i18nInstance.getDateTimeFormat(currentLocale) as Record<string, Intl.DateTimeFormatOptions> | undefined;

  let sourceKey: KnownI18nDateTimeFormat = formatKey;
  if (formatKey === 'shortDate' && allFormats?.shortDateTime) {
    sourceKey = 'shortDateTime';
  }

  const baseOptions: Intl.DateTimeFormatOptions = allFormats?.[sourceKey] ?? {};

  const { timeZone: _, ...optionsWithoutTimeZone } = baseOptions;

  let finalOptions: Intl.DateTimeFormatOptions = { ...optionsWithoutTimeZone };

  if (formatKey === 'shortDate') {
    finalOptions = sanitizeShortDate(finalOptions);
  }

  if (formatKey === 'shortTime') {
    finalOptions = sanitizeShortTime(finalOptions);
  }

  const formatter = new Intl.DateTimeFormat(currentLocale, finalOptions);

  return formatter.format(dateValue);
}
